import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, formatApiError, STATUS_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Cube, MagnifyingGlass, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import SortableHeader from "@/components/SortableHeader";
import WhatsAppNudge from "@/components/WhatsAppNudge";

const PAGE_SIZE = 10;

const emptyForm = {
  centre_id: "",
  asset_name: "",
  asset_type: "equipment",
  category: "",
  vendor_id: "",
  vendor: "",
  invoice_number: "",
  invoice_date: "",
  purchase_date: "",
  description: "",
  quantity: 1,
  unit_cost: 0,
  total_cost: 0,
  gst_pct: 0,
  gst_amount: 0,
  useful_life_years: "",
  serial_number: "",
  location: "",
};

export default function Assets() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [centres, setCentres] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [flt, setFlt] = useState({ q: "", status: "all", centre: "all", start_date: "", end_date: "" });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);

  const hasWildcard = user && (user.role === "admin" || user.all_centres);
  const assignedIds = hasWildcard ? centres.map((c) => c.id) : (user?.centre_ids || []);
  const zeroAccess = !hasWildcard && assignedIds.length === 0;

  const load = () => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    if (flt.q) params.set("q", flt.q);
    if (flt.status !== "all") params.set("status_filter", flt.status);
    if (flt.centre !== "all") params.set("centre_id", flt.centre);
    if (flt.start_date) params.set("start_date", flt.start_date);
    if (flt.end_date) params.set("end_date", flt.end_date);
    if (sortBy && sortDir) { params.set("sort_by", sortBy); params.set("sort_dir", sortDir); }
    api.get(`/assets?${params}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  };
  useEffect(() => {
    api.get("/centres").then((r) => setCentres(r.data));
    api.get("/vendors").then((r) => setVendors(r.data.items || r.data));
  }, []);
  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [flt.q, flt.status, flt.centre, flt.start_date, flt.end_date]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flt, page, sortBy, sortDir]);

  const applySort = (field, dir) => { setSortBy(field); setSortDir(dir); setPage(1); };
  const hdrProps = { sortBy, sortDir, onChange: applySort };

  const openCreate = () => {
    if (zeroAccess) {
      toast.error("No centre has been assigned to your account. Please contact Admin.");
      return;
    }
    const initial = { ...emptyForm };
    if (!hasWildcard && assignedIds.length === 1) initial.centre_id = assignedIds[0];
    setF(initial);
    setOpen(true);
  };

  const submit = async () => {
    if (!f.centre_id) return toast.error("Please select a centre");
    if (!f.asset_name?.trim()) return toast.error("Asset name is required");
    if (!f.description?.trim()) return toast.error("Description is mandatory.");
    try {
      const payload = {
        ...f,
        quantity: Number(f.quantity) || 1,
        unit_cost: Number(f.unit_cost) || 0,
        total_cost: Number(f.total_cost) || 0,
        gst_amount: Number(f.gst_amount) || 0,
        useful_life_years: f.useful_life_years ? Number(f.useful_life_years) : null,
      };
      await api.post("/assets", payload);
      toast.success("Asset submitted — awaiting first-level approval");
      setOpen(false);
      setF(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const centresAllowed = hasWildcard
    ? centres
    : centres.filter((c) => assignedIds.includes(c.id));

  return (
    <div className="p-8 space-y-6" data-testid="assets-root">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Capital</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Assets</h1>
          <p className="text-xs text-zinc-500 mt-2">
            Equipment, property, vehicles &amp; long-term purchases — separate from operational expenses.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-asset-btn"
          className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2">
          <Plus size={14} weight="bold" /> New Asset
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="relative md:col-span-2">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search asset / invoice / vendor" value={flt.q}
            onChange={(e) => setFlt({ ...flt, q: e.target.value })} className="rounded-sm h-10 pl-8"
            data-testid="assets-search" />
        </div>
        <Select value={flt.status} onValueChange={(v) => setFlt({ ...flt, status: v })}>
          <SelectTrigger className="rounded-sm h-10" data-testid="assets-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="in_review">In Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={flt.start_date}
          onChange={(e) => setFlt({ ...flt, start_date: e.target.value })}
          className="rounded-sm h-10" data-testid="assets-start-date" />
        <Input type="date" value={flt.end_date}
          onChange={(e) => setFlt({ ...flt, end_date: e.target.value })}
          className="rounded-sm h-10" data-testid="assets-end-date" />
      </div>

      {/* Filter-aware totals (spec §23) */}
      <div className="flex justify-end gap-6 bg-zinc-50 border border-zinc-200 px-4 py-3" data-testid="assets-total-strip">
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total records</div>
          <div className="font-heading text-lg tabular-nums" data-testid="assets-count">{total}</div>
        </div>
        <div className="text-right border-l border-zinc-200 pl-6">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total cost (this page)</div>
          <div className="font-heading text-lg tabular-nums text-emerald-800" data-testid="assets-total">
            {fmtINR(items.reduce((s, a) => s + Number(a.total_cost || 0), 0))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4"><SortableHeader field="asset_no" label="Asset #" {...hdrProps} /></th>
              <th className="text-left p-4"><SortableHeader field="asset_name" label="Name" {...hdrProps} /></th>
              <th className="text-left p-4">Centre</th>
              <th className="text-left p-4">Type</th>
              <th className="text-left p-4"><SortableHeader field="vendor" label="Vendor" {...hdrProps} /></th>
              <th className="text-right p-4"><SortableHeader field="total_cost" label="Total Cost" align="right" {...hdrProps} /></th>
              <th className="text-left p-4">Invoice</th>
              <th className="text-left p-4"><SortableHeader field="status" label="Status" {...hdrProps} /></th>
              <th className="text-left p-4">Next Approver</th>
              <th className="text-right p-4 w-14">Nudge</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {items.map((a) => (
              <tr key={a.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                data-testid={`asset-row-${a.asset_no}`}
                onClick={() => nav(`/assets/${a.id}`)}>
                <td className="p-4 font-mono text-xs">{a.asset_no}</td>
                <td className="p-4 font-medium">{a.asset_name}</td>
                <td className="p-4 text-zinc-600">{a.centre_code}</td>
                <td className="p-4 text-zinc-600 capitalize">{a.asset_type || "—"}</td>
                <td className="p-4 text-zinc-600">{a.vendor || "—"}</td>
                <td className="p-4 text-right">{fmtINR(a.total_cost)}</td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  {(a.attachments || []).length === 0 ? (
                    <span className="text-zinc-300 text-xs">—</span>
                  ) : (
                    <div className="flex gap-1 items-center" data-testid={`asset-invoice-${a.asset_no}`}>
                      {(a.attachments || []).slice(0, 3).map((att, idx) => (
                        <a key={att.file_id || att.filename || idx}
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${att.file_id}/download`}
                          target="_blank" rel="noopener noreferrer"
                          title={att.filename}
                          className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:text-black underline">
                          <Paperclip size={10} /> {(att.filename || "file").slice(0, 12)}
                        </a>
                      ))}
                      {(a.attachments || []).length > 3 && (
                        <span className="text-[10px] text-zinc-500">+{a.attachments.length - 3}</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${STATUS_COLORS[a.status] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="p-4 text-zinc-600">{a.next_approver_role || "—"}</td>
                <td className="p-2 text-right" onClick={(e) => e.stopPropagation()}>
                  {["submitted", "in_review"].includes(a.status) && (
                    <WhatsAppNudge kind="asset" id={a.id} number={a.asset_no} variant="icon" />
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={10} className="p-12 text-center">
                  <Cube size={32} className="mx-auto text-zinc-300 mb-3" />
                  <p className="text-sm text-zinc-500">No assets match your filters yet.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} pageSize={PAGE_SIZE} total={total}
          onPageChange={setPage} testid="assets-pagination" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">New Asset</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Capital purchases go through a 5-level sequential approval:
              Centre Manager → Coordinator → Project Head → Director → Finance.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-2">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Centre</Label>
              <Select value={f.centre_id} onValueChange={(v) => setF({ ...f, centre_id: v })}
                disabled={!hasWildcard && centresAllowed.length === 1}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="asset-centre"><SelectValue placeholder="Select centre" /></SelectTrigger>
                <SelectContent>
                  {centresAllowed.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Asset Name</Label>
              <Input value={f.asset_name} onChange={(e) => setF({ ...f, asset_name: e.target.value })}
                className="rounded-sm h-10 mt-2" data-testid="asset-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Type</Label>
              <Select value={f.asset_type} onValueChange={(v) => setF({ ...f, asset_type: v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="property">Property</SelectItem>
                  <SelectItem value="vehicle">Vehicle</SelectItem>
                  <SelectItem value="it">IT / Electronics</SelectItem>
                  <SelectItem value="furniture">Furniture</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Category</Label>
              <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="rounded-sm h-10 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Vendor</Label>
              <Select value={f.vendor_id} onValueChange={(v) => {
                const ven = vendors.find((x) => x.id === v);
                setF({ ...f, vendor_id: v, vendor: ven?.name || "" });
              }}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="asset-vendor"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Invoice #</Label>
              <Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} className="rounded-sm h-10 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Invoice Date</Label>
              <Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} className="rounded-sm h-10 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Purchase Date</Label>
              <Input type="date" value={f.purchase_date} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} className="rounded-sm h-10 mt-2" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Quantity</Label>
              <Input type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums text-right" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Unit Cost (₹)</Label>
              <Input type="number" value={f.unit_cost}
                onChange={(e) => {
                  const uc = Number(e.target.value) || 0;
                  const qty = Number(f.quantity) || 1;
                  const base = uc * qty;
                  const pct = Number(f.gst_pct) || 0;
                  const gst = Math.round(base * pct) / 100;
                  setF({ ...f, unit_cost: e.target.value, total_cost: Math.round((base + gst) * 100) / 100, gst_amount: gst });
                }}
                className="rounded-sm h-10 mt-2 tabular-nums text-right" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">GST %</Label>
              <Input type="number" step="0.01" value={f.gst_pct}
                onChange={(e) => {
                  const pct = Number(e.target.value) || 0;
                  const base = (Number(f.unit_cost) || 0) * (Number(f.quantity) || 1);
                  const gst = Math.round(base * pct) / 100;
                  setF({ ...f, gst_pct: e.target.value, gst_amount: gst, total_cost: Math.round((base + gst) * 100) / 100 });
                }}
                className="rounded-sm h-10 mt-2 tabular-nums text-right"
                placeholder="e.g. 18"
                data-testid="asset-gst-pct" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">GST Amount (auto)</Label>
              <Input type="number" value={f.gst_amount} readOnly
                className="rounded-sm h-10 mt-2 tabular-nums text-right bg-zinc-50"
                data-testid="asset-gst-amount" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Total Cost (₹)</Label>
              <Input type="number" value={f.total_cost} readOnly
                className="rounded-sm h-10 mt-2 tabular-nums text-right bg-emerald-50 border-emerald-300 font-medium"
                data-testid="asset-total-cost" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Useful Life (yrs)</Label>
              <Input type="number" value={f.useful_life_years} onChange={(e) => setF({ ...f, useful_life_years: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums text-right" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Serial #</Label>
              <Input value={f.serial_number} onChange={(e) => setF({ ...f, serial_number: e.target.value })} className="rounded-sm h-10 mt-2" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Location</Label>
              <Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="rounded-sm h-10 mt-2" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Description <span className="text-rose-600">*</span></Label>
              <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                required data-testid="asset-description"
                className="rounded-sm h-10 mt-2" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Invoice Attachment (PDF / JPG / PNG)</Label>
              <label className="mt-2 border-2 border-dashed border-zinc-300 rounded-sm p-4 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-zinc-50">
                <Paperclip size={16} />
                <div className="text-sm">Click to upload invoice</div>
                <div className="text-[10px] text-zinc-500">Accepts .pdf, .jpg, .jpeg, .png — max 15 MB</div>
                <input type="file" className="hidden" data-testid="asset-invoice-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      const entry = {
                        file_id: r.data.id,
                        filename: r.data.original_filename,
                        content_type: r.data.content_type,
                        kind: "invoice",
                      };
                      setF({ ...f, attachments: [...(f.attachments || []), entry] });
                      toast.success(`Attached ${file.name}`);
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    }
                    e.target.value = "";
                  }}
                />
              </label>
              {(f.attachments || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {(f.attachments || []).map((a, i) => (
                    <div key={a.file_id || a.filename || i} className="flex justify-between items-center px-3 py-2 bg-zinc-50 border border-zinc-200 text-xs">
                      <span className="flex items-center gap-2"><Paperclip size={12} />{a.filename}</span>
                      <button type="button" className="text-rose-600 hover:underline"
                        onClick={() => setF({ ...f, attachments: (f.attachments || []).filter((_, j) => j !== i) })}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} data-testid="save-asset"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
